/* eslint-disable @typescript-eslint/ban-ts-comment */
import React, { useContext, useEffect, useRef, useState } from 'react';
import type { DealerSearchProps } from '../../interfaces/header-components.interfaces';
import { FeatureAppLoader } from '@feature-hub/react';
import { fetchContent } from '@oneaudi/falcon-tools';
import styled from 'styled-components';
import { ConfigContext } from '../../context/ConfigContext';
import { ServiceContext } from '../../context/ServiceContext';
import type { ServiceConfigurationV1 } from '@volkswagen-onehub/gfa-service-config-provider';
import AppLoaderErrorBoundary from '../AppLoaderErrorBoundary/AppLoaderErrorBoundary';
import AudiSearchFeatureAppLoader from '../AudiHeaderSearch/AudiSearchFeatureAppLoader';

const StyledContainer = styled.div<{ visible: boolean; isMobileView?: boolean }>`
  display: ${({ visible }): string => (visible ? 'flex' : 'none')};
  flex-direction: column;
  align-self: center;
  width: 100%;
  padding-top: ${({ theme }): string => theme.spacing.l};
  ${({ isMobileView }): string => (!isMobileView ? `height: 80vh;` : '')};
  @media screen and (min-height: 745px) {
    padding-top: 0;
  }
`;

const DealerSearchFeatureAppLoader: React.FC<DealerSearchProps> = ({
  visible,
  isMobileView,
  dealerSearchGeolocationEnabled,
}) => {
  const { dealerSearchFeatureAppUrl, dealerSearchConfigPath } = useContext(ConfigContext);
  const { configProvider } = useContext(ServiceContext);
  let contentApiConfig: ServiceConfigurationV1 = {
    id: '',
  };
  if (typeof window !== 'undefined') {
    contentApiConfig = configProvider.audi['falcon-content-api'];
  }
  // default config to load Dealer Search in Header
  const [configData, setConfigData] = useState({
    intlSearchEnabled: true,
    oneHeader: true,
    connectDealer: true,
    autoConnectDealer: dealerSearchGeolocationEnabled,
    headerMobileView: isMobileView,
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await fetchContent(dealerSearchConfigPath.trim(), contentApiConfig);
        const dsFields = res.fields;

        // @ts-expect-error
        setConfigData((prevState: null | object) => {
          if (typeof dsFields === 'object') {
            return {
              ...(prevState || {}),
              ...(dsFields as unknown as object),
              // create filters object from config
              ...{
                filters: {
                  sales: dsFields?.sales ?? false,
                  aftersales: dsFields?.aftersales ?? false,
                  service: dsFields?.service ?? false,
                  usedcars: dsFields?.usedCars ?? false,
                  etron: dsFields?.etron ?? false,
                  'sales-rs': dsFields?.['sales-rs'] ?? false,
                  'sales-r8': dsFields?.['sales-r8'] ?? false,
                  'service-noagent': dsFields?.['service-noagent'] ?? false,
                  'audi-on-demand': dsFields?.['audi-on-demand'] ?? false,
                  'accident-specialist': dsFields?.['accident-specialist'] ?? false,
                  atyourdoor: dsFields?.atyourdoor ?? false,
                  'silver-car-by-audi': dsFields?.['silver-car-by-audi'] ?? false,
                  ovt: dsFields?.ovt ?? false,
                },
              },
              // create twoClick object from config
              ...{
                twoClick: {
                  headline: dsFields?.twoClickHeadline ?? '',
                  copyText: dsFields?.twoClickCopyText ?? '',
                  toggleLabel: dsFields?.twoClickToggleLabel ?? '',
                  asideToggleHeadline: dsFields?.twoClickAsideToggleHeadline ?? '',
                  asideToggleLabel: dsFields?.twoClickAsideToggleLabel ?? '',
                  asideToggleCopy: dsFields?.twoClickAsideToggleCopy ?? '',
                  closeButton: {
                    text: dsFields?.twoClickCloseButtonText ?? '',
                    redirectURL: dsFields?.twoClickCloseButtonRedirectURL ?? '',
                  },
                },
              },
              // create fiveStar object from config
              ...{
                fiveStar: {
                  displayFiveStarRating: dsFields?.displayFiveStarRating ?? false,
                  legalText: dsFields?.legalText ?? '',
                },
              },
              oneHeader: true,
              autoConnectDealer: dealerSearchGeolocationEnabled,
              headerMobileView: isMobileView, // DealerSearch FA uses this to determine if it should be rendered in mobile view
            };
          }
          return prevState;
        });
      } catch (err: unknown) {
        if (err instanceof Error && err.name !== 'AbortError') {
          console.warn(err);
        }
      } finally {
        // set delay to allow DS FA to load
        setTimeout(() => {
          setLoading(false);
        }, 1500);
      }
    };

    fetchData();
  }, [contentApiConfig, dealerSearchConfigPath, dealerSearchGeolocationEnabled, isMobileView]);

  useEffect(() => {
    if (!loading && visible) {
      const dealerSearchFAInput = document.getElementById('search-dealer-field');

      // Only focus the input, no scrolling needed as content is within popover
      requestAnimationFrame(() => {
        setTimeout(() => {
          dealerSearchFAInput?.focus();
        }, 200);
      });
    }
  }, [loading, visible]);

  const dealerSearch = useRef<HTMLDivElement>(null);

  return (
    <StyledContainer ref={dealerSearch} visible={visible} isMobileView={isMobileView}>
      <AppLoaderErrorBoundary>
        {loading ? (
          <AudiSearchFeatureAppLoader fromFA="dealersearch" />
        ) : (
          <FeatureAppLoader
            src={dealerSearchFeatureAppUrl}
            featureAppId="map-app"
            config={configData}
          />
        )}
      </AppLoaderErrorBoundary>
    </StyledContainer>
  );
};

export default DealerSearchFeatureAppLoader;
